import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DatePicker } from '../ui/date-picker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  AlertTriangle, Calendar, Clock, MapPin, Building2,
  CheckCircle2, ArrowRight, RotateCcw, MoreVertical,
  ArrowUp, ArrowDown, ArrowUpDown, Package, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import CancelJobDialog from '../CancelJobDialog';

const STATUS_LABELS = {
  booked: { label: 'Booked', cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40' },
  dispatched: { label: 'Dispatched', cls: 'bg-sky-500/15 text-sky-400 border-sky-500/40' },
  on_hire: { label: 'On Hire', cls: 'bg-[#9aff00]/15 text-[#9aff00] border-[#9aff00]/40' },
  off_hired: { label: 'Off-Hired', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/40' },
  closed: { label: 'Closed', cls: 'bg-zinc-700/30 text-zinc-400 border-zinc-700/50' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-500/15 text-red-400 border-red-500/40' },
};

const TAB_CONFIG = [
  { key: 'active', label: 'Active', filter: { status: 'active' } },
  { key: 'booked', label: 'Booked', filter: { status: 'booked' } },
  { key: 'on_hire', label: 'On Hire', filter: { status: 'on_hire' } },
  { key: 'off_hired', label: 'Off-Hired', filter: { status: 'off_hired' } },
  { key: 'closed', label: 'Closed', filter: { status: 'closed' } },
  { key: 'cancelled', label: 'Cancelled', filter: { status: 'cancelled' } },
];

const fmtDate = (d) => {
  if (!d) return null;
  try { return format(new Date(d + 'T00:00:00'), 'EEE, dd MMM yyyy'); }
  catch { return d; }
};

export function DryHireView({ api, onOpenJob, onAllocateAsset, refreshKey = 0 }) {
  const [activeTab, setActiveTab] = useState('active');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [offHireDialog, setOffHireDialog] = useState(null); // { job, off_hire_date }
  const [cancelDialog, setCancelDialog] = useState(null); // { job }
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const tab = TAB_CONFIG.find(t => t.key === activeTab);
      const params = new URLSearchParams(tab?.filter || {});
      const { data } = await api.get(`/dry-hires?tower_crane=false&${params.toString()}`);
      setItems(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load dry-hires');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeTab, refreshKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      String(it.job_number || '').includes(q) ||
      (it.customer_name || '').toLowerCase().includes(q) ||
      (it.equipment_type || '').toLowerCase().includes(q) ||
      (it.asset_id || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  // Sorting — client-side. Click column header once=asc, twice=desc, thrice=clear.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc'); // asc | desc

  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null);
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) return filtered;
    const getVal = (r) => {
      switch (sortKey) {
        case 'job': return r.job_number ?? 0;
        case 'customer': return (r.customer_name || '').toLowerCase();
        case 'asset': return `${(r.equipment_type || '').toLowerCase()} ${r.asset_id || ''}`;
        case 'dates': return r.start_date || '';
        case 'duration': return r.days_on_hire ?? -1;
        default: return '';
      }
    };
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = getVal(a); const bv = getVal(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const overdueCount = items.filter(i => i.is_overdue).length;

  const transition = async (job, newStatus, extra = {}) => {
    setTransitioning(true);
    try {
      await api.post(`/jobs/${job.id}/hire-status`, { new_status: newStatus, ...extra });
      toast.success(`Marked ${STATUS_LABELS[newStatus]?.label || newStatus}`);
      setOffHireDialog(null);
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transition failed');
    } finally {
      setTransitioning(false);
    }
  };

  return (
    <Card className="card-industrial" data-testid="dry-hire-view">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            Dry-Hire
            {overdueCount > 0 && (
              <Badge variant="outline" className="bg-amber-500/15 text-amber-400 border-amber-500/40 ml-2 gap-1" data-testid="dryhire-overdue-badge">
                <AlertTriangle className="h-3 w-3" />
                {overdueCount} overdue
              </Badge>
            )}
          </CardTitle>
          <Input
            placeholder="Search job #, customer, asset…"
            className="w-full sm:w-72"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="dryhire-search-input"
          />
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 flex-wrap" data-testid="dryhire-status-tabs">
            {TAB_CONFIG.map(t => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`dryhire-tab-${t.key}`}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {TAB_CONFIG.map(t => (
            <TabsContent key={t.key} value={t.key} className="mt-0">
              {loading ? (
                <div className="text-sm text-muted-foreground py-12 text-center">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="text-sm text-muted-foreground py-12 text-center" data-testid={`dryhire-empty-${t.key}`}>
                  No dry-hire jobs in this view.
                </div>
              ) : (
                <div className="rounded-lg border border-zinc-800 overflow-hidden" data-testid={`dryhire-list-${t.key}`}>
                  <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                    <colgroup><col style={{ width: '160px' }} /><col /><col /><col className="hidden lg:table-column" /><col style={{ width: '110px' }} className="hidden xl:table-column" /><col style={{ width: '60px' }} /></colgroup>
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wide text-muted-foreground/80 font-semibold bg-zinc-900/60 select-none">
                        <SortableTh label="Job"      sortKey="job"      activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="left" />
                        <SortableTh label="Customer" sortKey="customer" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="left" />
                        <SortableTh label="Asset"    sortKey="asset"    activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="left" />
                        <SortableTh label="Dates"    sortKey="dates"    activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="left" className="hidden lg:table-cell" />
                        <SortableTh label="Duration" sortKey="duration" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="left" className="hidden xl:table-cell" />
                        <th className="text-right px-4 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map(job => (
                        <DryHireRow
                          key={job.id}
                          job={job}
                          onOpenJob={onOpenJob}
                          onAllocateAsset={onAllocateAsset}
                          onRequestOffHire={(j) => setOffHireDialog({ job: j, off_hire_date: format(new Date(), 'yyyy-MM-dd') })}
                          onRequestCancel={(j) => setCancelDialog({ job: j })}
                          onTransition={transition}
                          busy={transitioning}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>

      {/* Off-Hire Dialog */}
      <Dialog open={!!offHireDialog} onOpenChange={(o) => !o && setOffHireDialog(null)}>
        <DialogContent data-testid="off-hire-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Off-Hire — Job #{offHireDialog?.job?.job_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              {offHireDialog?.job?.customer_name} · {offHireDialog?.job?.equipment_type} ({offHireDialog?.job?.asset_id || '—'})
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide">Off-Hire Date</Label>
              <DatePicker
                value={offHireDialog?.off_hire_date || ''}
                minDate={offHireDialog?.job?.start_date}
                onChange={(date) => setOffHireDialog(prev => ({ ...prev, off_hire_date: date || '' }))}
                testId="off-hire-date-input"
                className="mt-1 w-full"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                The asset becomes available again from this date. A post-hire inspection will be auto-created.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOffHireDialog(null)}>Cancel</Button>
            <Button
              className="btn-volt"
              disabled={transitioning || !offHireDialog?.off_hire_date}
              onClick={() => transition(offHireDialog.job, 'off_hired', { off_hire_date: offHireDialog.off_hire_date })}
              data-testid="off-hire-confirm-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Confirm Off-Hire
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Job Dialog — reuses the shared CancelJobDialog from wet-hire */}
      <CancelJobDialog
        open={!!cancelDialog}
        onOpenChange={(o) => !o && setCancelDialog(null)}
        job={cancelDialog?.job}
        customerEmail={cancelDialog?.job?.customer_email}
        onConfirm={async ({ reason, notes, send_email }) => {
          setCancelSubmitting(true);
          try {
            await api.post(`/jobs/${cancelDialog.job.id}/cancel`, { reason, notes, send_email });
            toast.success('Job cancelled');
            setCancelDialog(null);
            await fetchItems();
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to cancel job');
          } finally {
            setCancelSubmitting(false);
          }
        }}
        submitting={cancelSubmitting}
      />
    </Card>
  );
}

function SortableTh({ label, sortKey, activeKey, dir, onSort, align = 'left', className = '' }) {
  const isActive = activeKey === sortKey;
  const Icon = !isActive ? ArrowUpDown : (dir === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th className={`px-4 py-2 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 transition-colors ${isActive ? 'text-[#9aff00]' : 'hover:text-zinc-200'}`}
        data-testid={`dryhire-sort-${sortKey}`}
      >
        <span>{label}</span>
        <Icon className={`h-3 w-3 ${isActive ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
}

function DryHireRow({ job, onOpenJob, onAllocateAsset, onRequestOffHire, onRequestCancel, onTransition, busy }) {
  const status = STATUS_LABELS[job.hire_status] || STATUS_LABELS.booked;
  const isActive = ['booked', 'dispatched', 'on_hire'].includes(job.hire_status);
  const isOpenEnded = !job.end_date;
  const hasAllocation = !!job.primary_allocation;

  // Build menu items from the state machine so only valid transitions appear.
  const menuItems = [];
  if (!hasAllocation && isActive) {
    menuItems.push({ key: 'allocate', label: 'Allocate Asset', icon: Package, action: () => onAllocateAsset?.(job) });
  }
  if (job.hire_status === 'booked') {
    menuItems.push({ key: 'dispatched', label: 'Mark Dispatched', icon: ArrowRight, action: () => onTransition(job, 'dispatched') });
  }
  if (job.hire_status === 'dispatched') {
    menuItems.push({ key: 'on_hire', label: 'Mark On-Hire', icon: ArrowRight, action: () => onTransition(job, 'on_hire') });
  }
  if (['dispatched', 'on_hire'].includes(job.hire_status)) {
    menuItems.push({ key: 'off_hired', label: 'Off-Hire…', icon: RotateCcw, action: () => onRequestOffHire(job) });
  }
  if (job.hire_status === 'off_hired') {
    menuItems.push({ key: 'closed', label: 'Close', icon: CheckCircle2, action: () => onTransition(job, 'closed') });
  }
  if (['booked'].includes(job.hire_status)) {
    menuItems.push({ key: 'cancelled', label: 'Cancel Job', icon: Ban, action: () => onRequestCancel(job), destructive: true });
  }

  // Handler: stop event bubbling so cell clicks don't also fire the row click.
  const stop = (e) => e.stopPropagation();

  return (
    <tr
      className="border-t border-zinc-800 hover:bg-zinc-900/50 transition-colors align-top cursor-pointer"
      onClick={() => onOpenJob?.(job)}
      data-testid={`dryhire-card-${job.id}`}
    >
      {/* Col 1 — Job # + status pills */}
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-heading font-semibold" data-testid={`dryhire-job-link-${job.id}`}>
            Job #{job.job_number}
          </span>
          <Badge variant="outline" className={`text-[10px] leading-none py-0.5 ${status.cls}`} data-testid={`dryhire-status-pill-${job.id}`}>
            {status.label}
          </Badge>
          {isOpenEnded && isActive && (
            <Badge variant="outline" className="text-[10px] leading-none py-0.5 bg-zinc-800 text-zinc-300 border-zinc-700" data-testid={`dryhire-openended-pill-${job.id}`}>
              Open-Ended
            </Badge>
          )}
          {job.is_overdue && (
            <Badge variant="outline" className="text-[10px] leading-none py-0.5 bg-amber-500/15 text-amber-400 border-amber-500/40 gap-1" data-testid={`dryhire-overdue-pill-${job.id}`}>
              <AlertTriangle className="h-3 w-3" /> Overdue
            </Badge>
          )}
        </div>
      </td>

      {/* Col 2 — Customer + site (with date fallback when Dates column is hidden < lg) */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 truncate">
          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate" title={job.customer_name || ''}>{job.customer_name || '—'}</span>
        </div>
        {job.site_name && (
          <div className="flex items-center gap-1.5 truncate text-[11px] text-muted-foreground mt-0.5 pl-5">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate" title={`${job.site_name}${job.site_suburb ? ' · ' + job.site_suburb : ''}`}>
              {job.site_name}{job.site_suburb ? ` · ${job.site_suburb}` : ''}
            </span>
          </div>
        )}
        {/* Fallback: when Dates column is hidden (< lg breakpoint), surface the
            date range in the Customer cell so it's never lost. */}
        <div className="lg:hidden flex items-center gap-1.5 text-[11px] text-muted-foreground mt-0.5 pl-5">
          <Calendar className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {fmtDate(job.start_date)}
            {job.end_date ? ` → ${fmtDate(job.end_date)}` : ' → open-ended'}
          </span>
        </div>
      </td>

      {/* Col 3 — Asset (clickable → allocation) */}
      <td
        className="px-4 py-3 truncate group/asset"
        onClick={(e) => { stop(e); onAllocateAsset?.(job); }}
        title={hasAllocation ? 'Edit allocation' : 'Allocate asset'}
        data-testid={`dryhire-asset-cell-${job.id}`}
      >
        <div className="inline-flex items-center gap-1.5 rounded-md px-1.5 -mx-1.5 py-0.5 group-hover/asset:bg-[#9aff00]/10 group-hover/asset:text-[#9aff00] transition-colors truncate max-w-full">
          <Package className="h-3.5 w-3.5 shrink-0 opacity-60 group-hover/asset:opacity-100" />
          <span className="truncate">
            {job.equipment_type || <span className="italic text-muted-foreground">Allocate asset…</span>}
            {job.asset_id && <span className="text-muted-foreground group-hover/asset:text-[#9aff00]/80"> · {job.asset_id}</span>}
          </span>
        </div>
      </td>

      {/* Col 4 — Dates (hidden on screens < lg, fallback shows in Customer cell) */}
      <td className="px-4 py-3 hidden lg:table-cell">
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">
            {fmtDate(job.start_date)}
            {job.end_date ? <> → {fmtDate(job.end_date)}</> : <span className="text-muted-foreground"> → open-ended</span>}
          </span>
        </div>
      </td>

      {/* Col 5 — Duration + expected return (hidden on screens < xl) */}
      <td className="px-4 py-3 hidden xl:table-cell">
        <div className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span>{job.days_on_hire != null ? `${job.days_on_hire} day${job.days_on_hire === 1 ? '' : 's'}` : '—'}</span>
        </div>
        {job.expected_off_hire_date && isActive && (
          <div className="text-[11px] text-muted-foreground mt-0.5 pl-5 truncate">
            exp. {fmtDate(job.expected_off_hire_date)}
          </div>
        )}
      </td>

      {/* Col 6 — Actions (dropdown menu) */}
      <td className="px-4 py-3 text-right" onClick={stop}>
        {menuItems.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={busy}
                data-testid={`dryhire-actions-trigger-${job.id}`}
                onClick={stop}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48" onClick={stop}>
              {menuItems.map((m, idx) => {
                const Icon = m.icon;
                return (
                  <React.Fragment key={m.key}>
                    {idx > 0 && (m.key === 'off_hired' || m.key === 'cancelled') && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      onSelect={(e) => { e.preventDefault(); m.action(); }}
                      className={m.destructive ? 'text-red-400 focus:text-red-400' : ''}
                      data-testid={`dryhire-action-${m.key}-${job.id}`}
                    >
                      <Icon className="h-3.5 w-3.5 mr-2" />
                      {m.label}
                    </DropdownMenuItem>
                  </React.Fragment>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className="text-xs text-muted-foreground/60">—</span>
        )}
      </td>
    </tr>
  );
}

export default DryHireView;
